import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Collapse,
  IconButton,
  Chip
} from "@material-ui/core";
import {
  Cake as CakeIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    flexDirection: "column"
  },
  debugContainer: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    fontFamily: "monospace",
    fontSize: "0.8rem",
    maxHeight: 300,
    overflow: "auto"
  },
  errorAlert: {
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  successAlert: {
    backgroundColor: "#e8f5e8",
    border: "1px solid #4caf50",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

// Componente de Switch seguindo padrão IA.js
const SettingSwitch = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  disabled = false,
  icon,
  color = "primary"
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.checked);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value}
          onChange={handleChange}
          disabled={disabled}
          style={{ marginRight: '8px' }}
        />
        <Typography variant="body2">
          {value ? i18n.t("birthdaySettings.enabled") : i18n.t("birthdaySettings.disabled")}
        </Typography>
      </label>
    </Box>
  );
});

// Componente de TextField seguindo padrão IA.js
const SettingTextField = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  icon,
  type = "text",
  multiline = false,
  rows = 1,
  disabled = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <input
        type={type}
        value={value || ""}
        onChange={handleChange}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit',
          ...(multiline && { height: 'auto', minHeight: `${rows * 20}px`, resize: 'vertical' })
        }}
      />
    </Box>
  );
});

const BirthdaySettings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [showDebug, setShowDebug] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    user: true,
    contact: true,
    general: true
  });

  const addDebugInfo = useCallback((info) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugInfo(prev => `${prev}\n[${timestamp}] ${info}`);
    console.log(`🐛 [DEBUG] ${info}`);
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    addDebugInfo("🔍 " + i18n.t("birthdaySettings.debug.fetchingSettings"));

    try {
      addDebugInfo(`👤 ${i18n.t("birthdaySettings.debug.userInfo", {
        name: user?.name,
        id: user?.id,
        companyId: user?.companyId
      })}`);

      const { data } = await api.get("/birthdays/settings");

      addDebugInfo(`✅ ${i18n.t("birthdaySettings.debug.responseReceived")}: ${JSON.stringify(data, null, 2)}`);

      const settingsData = data?.data || data || {};
      setSettings(settingsData);
      addDebugInfo("✅ " + i18n.t("birthdaySettings.debug.settingsLoaded"));

    } catch (error) {
      addDebugInfo(`❌ ${i18n.t("birthdaySettings.debug.requestError")}: ${error.message}`);

      if (error.response?.status === 404) {
        setError(i18n.t("birthdaySettings.errors.routeNotFound"));
      } else if (error.response?.status === 401) {
        setError(i18n.t("birthdaySettings.errors.unauthorized"));
      } else if (error.response?.status >= 500) {
        setError(i18n.t("birthdaySettings.errors.serverError"));
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        setError(i18n.t("birthdaySettings.errors.networkError"));
      } else {
        setError(`${i18n.t("birthdaySettings.errors.generic")}: ${error.message}`);
      }

      toastUtils.error(i18n.t("birthdaySettings.errors.title"), i18n.t("birthdaySettings.errors.loadFailed"));

      // Dados padrão
      setSettings({
        userBirthdayEnabled: true,
        contactBirthdayEnabled: false,
        createAnnouncementForUsers: false,
        userBirthdayMessage: i18n.t("birthdaySettings.defaultMessages.userBirthday"),
        contactBirthdayMessage: i18n.t("birthdaySettings.defaultMessages.contactBirthday"),
        sendBirthdayTime: "09:00:00"
      });

    } finally {
      setLoading(false);
      addDebugInfo("🏁 " + i18n.t("birthdaySettings.debug.fetchCompleted"));
    }
  }, [user, addDebugInfo]);

  useEffect(() => {
    addDebugInfo("🚀 " + i18n.t("birthdaySettings.debug.componentMounted"));
    fetchSettings();
  }, [fetchSettings, addDebugInfo]);

  const handleSettingChange = useCallback((field, value) => {
    addDebugInfo(`📝 ${i18n.t("birthdaySettings.debug.settingChanged", { field, value })}`);
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  }, [addDebugInfo]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    addDebugInfo("💾 " + i18n.t("birthdaySettings.debug.saving"));

    try {
      await api.put("/birthdays/settings", settings);
      addDebugInfo("✅ " + i18n.t("birthdaySettings.debug.saveSuccess"));
      toastUtils.success(i18n.t("birthdaySettings.messages.saveSuccess"), i18n.t("birthdaySettings.messages.saveSuccessDesc"));
    } catch (error) {
      addDebugInfo(`❌ ${i18n.t("birthdaySettings.debug.saveError")}: ${error.message}`);
      toastUtils.error(i18n.t("birthdaySettings.messages.saveError"), error.message);
    } finally {
      setSaving(false);
    }
  }, [settings, addDebugInfo]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: '16px' }}>
          {i18n.t("birthdaySettings.loading")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <CakeIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("birthdaySettings.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("birthdaySettings.description")}
        </Typography>

        {error && (
          <Box className={classes.errorAlert}>
            <Box display="flex" alignItems="center" style={{ marginBottom: '8px' }}>
              <ErrorIcon style={{ marginRight: '8px', color: '#f44336' }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {i18n.t("birthdaySettings.errors.title")}
              </Typography>
            </Box>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? i18n.t("birthdaySettings.saving") : i18n.t("birthdaySettings.save")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSettings}
            >
              {i18n.t("birthdaySettings.refresh")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<BugIcon />}
              onClick={() => setShowDebug(!showDebug)}
            >
              {i18n.t("birthdaySettings.debug.toggle")}
            </Button>
          </Grid>
        </Grid>

        {showDebug && (
          <Box className={classes.debugContainer}>
            <Typography variant="caption" style={{ fontWeight: 600 }}>
              {i18n.t("birthdaySettings.debug.title")}:
            </Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '11px' }}>
              {debugInfo}
            </pre>
          </Box>
        )}
      </Box>

      {/* Configurações de Usuários */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<PersonIcon />}
          title={i18n.t("birthdaySettings.sections.userBirthdays")}
          action={
            <IconButton onClick={() => toggleSection('user')}>
              {expandedSections.user ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.user}>
          <CardContent>
            <SettingSwitch
              id="userBirthdayEnabled"
              title={i18n.t("birthdaySettings.userBirthdayEnabled.title")}
              description={i18n.t("birthdaySettings.userBirthdayEnabled.description")}
              value={settings?.userBirthdayEnabled || false}
              onChange={handleSettingChange}
              icon={<PersonIcon className={classes.sectionIcon} />}
            />

            {settings?.userBirthdayEnabled && (
              <>
                <SettingSwitch
                  id="createAnnouncementForUsers"
                  title={i18n.t("birthdaySettings.createAnnouncementForUsers.title")}
                  description={i18n.t("birthdaySettings.createAnnouncementForUsers.description")}
                  value={settings?.createAnnouncementForUsers || false}
                  onChange={handleSettingChange}
                  icon={<CakeIcon className={classes.sectionIcon} />}
                />

                <SettingTextField
                  id="userBirthdayMessage"
                  title={i18n.t("birthdaySettings.userBirthdayMessage.title")}
                  description={i18n.t("birthdaySettings.userBirthdayMessage.description")}
                  value={settings?.userBirthdayMessage || ""}
                  onChange={handleSettingChange}
                  icon={<CakeIcon className={classes.sectionIcon} />}
                  multiline
                  rows={3}
                />
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Contatos */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<PersonIcon />}
          title={i18n.t("birthdaySettings.sections.contactBirthdays")}
          action={
            <IconButton onClick={() => toggleSection('contact')}>
              {expandedSections.contact ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.contact}>
          <CardContent>
            <SettingSwitch
              id="contactBirthdayEnabled"
              title={i18n.t("birthdaySettings.contactBirthdayEnabled.title")}
              description={i18n.t("birthdaySettings.contactBirthdayEnabled.description")}
              value={settings?.contactBirthdayEnabled || false}
              onChange={handleSettingChange}
              icon={<PersonIcon className={classes.sectionIcon} />}
            />

            {settings?.contactBirthdayEnabled && (
              <SettingTextField
                id="contactBirthdayMessage"
                title={i18n.t("birthdaySettings.contactBirthdayMessage.title")}
                description={i18n.t("birthdaySettings.contactBirthdayMessage.description")}
                value={settings?.contactBirthdayMessage || ""}
                onChange={handleSettingChange}
                icon={<CakeIcon className={classes.sectionIcon} />}
                multiline
                rows={3}
              />
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações Gerais */}
      <Card>
        <CardHeader
          avatar={<ScheduleIcon />}
          title={i18n.t("birthdaySettings.sections.generalSettings")}
          action={
            <IconButton onClick={() => toggleSection('general')}>
              {expandedSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.general}>
          <CardContent>
            <SettingTextField
              id="sendBirthdayTime"
              title={i18n.t("birthdaySettings.sendBirthdayTime.title")}
              description={i18n.t("birthdaySettings.sendBirthdayTime.description")}
              value={settings?.sendBirthdayTime || "09:00:00"}
              onChange={handleSettingChange}
              icon={<ScheduleIcon className={classes.sectionIcon} />}
              type="time"
            />
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
};

export default BirthdaySettings;